import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import { palette, graph } from '../../utils/designTokens';
import ServiceChips from './components/shared/ServiceChips';
import GraphNode from '../../components/GraphNode';
import { layoutNodesCustom } from '../../utils/graphLayout';
import {
  EMPTY_DATA_MESSAGE,
  GRAPH_MINIMAP_SHOW,
  GRAPH_MINIMAP_HIDE,
  GRAPH_MINIMAP_LABEL
} from '../../utils/strings';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { logger } from '../../utils/logger';
import { buildSummaryData } from '../../utils/traceSummaryTree';

/**
 * GraphTabV2 - Rewritten graph visualization with tab-based span details
 *
 * Key improvements over GraphTab.js:
 * - No decorator/badge system on nodes (tabs replace attribute badges)
 * - No concept nodes (system prompt inline)
 * - Cleaner component boundaries and error handling
 * - Tab-based UI for span details
 *
 * Preserves from GraphTab.js:
 * - Resizable panel system (20% default, 15-50% constraints)
 * - ReactFlow configuration (performance opts, interaction settings)
 * - MiniMap with design token colors
 * - Edge styling (color by destination node, red for errors/security)
 * - Auto-select root span on mount
 * - Dagre layout with custom Y-axis positioning
 */
const GraphTabV2 = ({ traceId, spans, selectedSpan, onSpanSelect, filterMode = 'summary', onFilterModeChange, participants = [] }) => {
  logger.debug('GraphTabV2 rendering:', { traceId, spanCount: spans?.length });
  const [showMiniMap, setShowMiniMap] = useState(true); // Show by default for better navigation
  const [isInteractive, setIsInteractive] = useState(true);

  // Dedupe spans by span_id (handles duplicate exports from BatchSpanProcessor)
  const graphNodes = useMemo(() => {
    if (!spans) return [];
    const seen = new Set();
    return spans.filter(span => {
      if (seen.has(span.span_id)) return false;
      seen.add(span.span_id);
      return true;
    });
  }, [spans]);

  // Stable across filterMode toggles — only recomputes when graphNodes change
  const summaryData = useMemo(() => buildSummaryData(graphNodes), [graphNodes]);

  const filteredGraphNodes = useMemo(() => {
    if (filterMode === 'all') return graphNodes;
    return graphNodes.filter(s => summaryData.ids.has(s.span_id));
  }, [graphNodes, filterMode, summaryData]);

  // O(1) lookup map for edge coloring (avoids O(n²) find inside the forEach)
  const filteredSpanMap = useMemo(() => {
    const map = new Map();
    filteredGraphNodes.forEach(s => map.set(s.span_id, s));
    return map;
  }, [filteredGraphNodes]);

  // Define custom node types
  const nodeTypes = useMemo(() => ({
    graphNode: GraphNode
  }), []);

  // Auto-select root span node once data is loaded (preserved from GraphTab.js lines 121-141)
  useEffect(() => {
    if (filteredGraphNodes.length > 0 && !selectedSpan) {
      const rootSpan = filteredGraphNodes.find(span =>
        !span.parent_span_id || span.parent_span_id === ''
      );
      if (rootSpan) {
        onSpanSelect(rootSpan);
      }
    }
  }, [filteredGraphNodes, selectedSpan, onSpanSelect]);

  // Helper function to get destination node stroke color (preserved from GraphTab.js lines 144-155)
  const getNodeStrokeColor = (nodeType) => {
    if (graph.nodes.entity[nodeType]) {
      return graph.nodes.entity[nodeType].stroke;
    }
    if (graph.nodes.concept[nodeType]) {
      return graph.nodes.concept[nodeType].stroke;
    }
    return palette.accents.primary.base;
  };

  const { nodes, edges } = useMemo(() => {
    if (!filteredGraphNodes.length) return { nodes: [], edges: [] };

    const tempNodes = [];
    const tempEdges = [];

    filteredGraphNodes.forEach(span => {
      const nodeType = span.span_kind?.toLowerCase() || 'unspecified';

      // In summary mode, a node is a root if the summary tree says it has no parent.
      // In all mode, use the raw parent_span_id.
      const effectiveParentId = filterMode === 'summary'
        ? summaryData.parentMap.get(span.span_id)
        : span.parent_span_id;
      const isRootSpan = !effectiveParentId;

      const hasSecurityIssues = Array.isArray(span.detections) && span.detections.length > 0;
      const hasError = span.status_code === 'ERROR' || span.metrics?.has_error;

      tempNodes.push({
        id: span.span_id,
        type: 'graphNode',
        position: { x: 0, y: 0 },
        data: {
          id: span.span_id,
          label: span.name,
          type: nodeType,
          nodeCategory: 'entity',
          isRootSpan,
          hasSecurityIssues,
          isDanger: hasError,
          detections: span.detections || [],
          llm_model_name: span.llm_model_name,
          tool_name: span.tool_name,
          status_code: span.status_code
        }
      });

      if (effectiveParentId && filteredSpanMap.has(effectiveParentId)) {
        const parentSpan = filteredSpanMap.get(effectiveParentId);
        const parentNodeType = parentSpan?.span_kind?.toLowerCase() || 'unspecified';
        const edgeColor = getNodeStrokeColor(parentNodeType);

        tempEdges.push({
          id: `${effectiveParentId}-${span.span_id}`,
          source: effectiveParentId,
          target: span.span_id,
          sourceHandle: 'right',
          targetHandle: 'left',
          style: { stroke: edgeColor, strokeWidth: 1 },
          markerEnd: { type: 'arrowclosed', color: edgeColor }
        });
      }
    });

    const layoutedNodes = layoutNodesCustom(tempNodes, tempEdges);
    const nodesWithSelection = layoutedNodes.map(node => ({
      ...node,
      selected: selectedSpan?.span_id === node.id
    }));

    return { nodes: nodesWithSelection, edges: tempEdges };
  }, [filteredGraphNodes, selectedSpan, filterMode, summaryData, filteredSpanMap]);

  const onNodeClick = useCallback((event, node) => {
    event.stopPropagation();
    logger.debug('Node clicked:', node.id);

    // Find full span object
    const span = filteredGraphNodes.find(s => s.span_id === node.id);
    if (span) {
      onSpanSelect(span);
    }
  }, [filteredGraphNodes, onSpanSelect]);

  const onPaneClick = useCallback(() => {
    // Clicking blank canvas has no effect - keep current selection
  }, []);

  // Render empty state or graph - no early returns to comply with Rules of Hooks
  return (
    <ErrorBoundary>
      {!graphNodes.length ? (
        <div className="flex h-full items-center justify-center">
          <div className="text-text-muted">{EMPTY_DATA_MESSAGE}</div>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-background-primary">
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-muted">
                {filterMode === 'summary' ? 'Showing key spans' : 'Showing all spans'}
              </span>
              <ServiceChips participants={participants} />
            </div>
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border border-border-subtle bg-background-tertiary hover:bg-background-quaternary text-text-primary"
              onClick={() => onFilterModeChange(filterMode === 'summary' ? 'all' : 'summary')}
            >
              {filterMode === 'summary' ? 'Show All' : 'Show Summary'}
            </button>
          </div>
          <div className="flex-1 min-h-0">
            {!filteredGraphNodes.length ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-text-muted text-sm">No key spans found. Click &ldquo;Show All&rdquo; to view all spans.</p>
              </div>
            ) : (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={true}
                fitView
                proOptions={{ hideAttribution: true }}
                panOnDrag={isInteractive}
                zoomOnScroll={isInteractive}
                zoomOnPinch={isInteractive}
                zoomOnDoubleClick={isInteractive}
                style={{ background: palette.neutrals.canvas }}
                onlyRenderVisibleElements={true}
                elevateNodesOnSelect={false}
                nodeOrigin={[0.5, 0.5]}
              >
                <Controls
                  showInteractive={true}
                  onInteractiveChange={(interactive) => setIsInteractive(interactive)}
                />
                {showMiniMap && (
                  <MiniMap
                    nodeColor={palette.text.primary}
                    nodeStrokeColor={palette.text.primary}
                    maskColor={`rgba(0, 0, 0, 0.7)`}
                    style={{
                      background: palette.neutrals.canvas,
                      border: `2px solid ${palette.neutrals.borderStrong}`,
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
                    }}
                    position="bottom-right"
                    nodeBorderRadius={4}
                    nodeStrokeWidth={10}
                    zoomable
                    pannable
                  />
                )}
                <div
                  className="absolute z-10"
                  style={{ bottom: '20px', right: '20px' }}
                >
                  <button
                    onClick={() => setShowMiniMap(!showMiniMap)}
                    className="px-3 py-1.5 text-xs bg-background-secondary border border-border-subtle rounded hover:bg-surface-hover text-text-secondary hover:text-text-primary transition-colors shadow-md"
                    title={showMiniMap ? "Hide navigation minimap" : "Show navigation minimap"}
                  >
                    {showMiniMap ? GRAPH_MINIMAP_HIDE : GRAPH_MINIMAP_SHOW} {GRAPH_MINIMAP_LABEL}
                  </button>
                </div>
              </ReactFlow>
            )}
          </div>
        </div>
        )}
    </ErrorBoundary>
  );
};

export default GraphTabV2;
